import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  Plus, Save, X, CheckCircle2, XCircle, Pause, Play, StopCircle, Copy,
  Trash2, LayoutGrid, Megaphone, ImagePlus,
} from 'lucide-react';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import AdminConfirm from '@/components/AdminConfirm';
import ImageUpload from '@/components/ImageUpload';
import SafeImg from '@/components/SafeImg';
import { FilterBar, SelectPill, Pagination, StateMsg } from '@/components/AdminFilters';

// Ad type internal codes → Korean labels shown to admins.
const AD_TYPES = [
  { value: 'home_banner',         label: '메인 배너 광고' },
  { value: 'home_schedule_top',   label: '홈 오늘 방송 예정 상단' },
  { value: 'schedule_top',        label: '편성표 상단' },
  { value: 'category_banner',     label: '카테고리 광고' },
  { value: 'featured_seller',     label: '추천 판매자 광고' },
  { value: 'popup',               label: '팝업 광고' },
];
const STATUS_LABELS = {
  draft: '임시저장', pending: '승인대기', approved: '승인', running: '진행중',
  rejected: '반려', paused: '일시정지', ended: '종료',
};
const STATUS_TONE = {
  draft: 'bg-gray-500/10 text-gray-500', pending: 'bg-amber-500/10 text-amber-600',
  approved: 'bg-blue-500/10 text-blue-600', running: 'bg-emerald-500/10 text-emerald-600',
  rejected: 'bg-red-500/10 text-red-600', paused: 'bg-purple-500/10 text-purple-600',
  ended: 'bg-black/10 text-gray-600',
};
const TARGET_TYPES = [
  { value: 'external', label: '외부 URL' }, { value: 'broadcast', label: '방송' },
  { value: 'seller', label: '판매자' }, { value: 'product', label: '상품' },
  { value: 'category', label: '카테고리' }, { value: 'schedule', label: '편성표' },
];

const AdminAds = ({ user }) => {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') === 'slots' ? 'slots' : 'campaigns';
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const p = new URLSearchParams(params); p.set('tab', tab); setParams(p, { replace: true });
  }, [tab]);  // eslint-disable-line

  return (
    <AdminLayout user={user} active="ads">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>광고 관리</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          광고 캠페인과 노출 슬롯을 분리해 관리합니다. 캠페인 노출 위치·기간·우선순위는 슬롯 연결로 결정됩니다.
        </p>
      </div>

      <div className="flex gap-1 mb-4 border-b border-black/5 dark:border-white/10">
        <TabBtn active={tab === 'campaigns'} onClick={() => setTab('campaigns')} icon={Megaphone} label="광고 캠페인" testid="ads-tab-campaigns" />
        <TabBtn active={tab === 'slots'}     onClick={() => setTab('slots')}     icon={LayoutGrid} label="광고 슬롯 관리" testid="ads-tab-slots" />
      </div>

      {tab === 'campaigns' ? <CampaignsTab user={user} /> : <SlotsTab user={user} />}
    </AdminLayout>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button type="button" onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 ${active ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'}`}
    data-testid={testid}>
    <Icon size={14} /> {label}
  </button>
);

/* ======================== CAMPAIGNS TAB ======================== */
const CampaignsTab = ({ user }) => {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [adType, setAdType] = useState('all');
  // STEP GROWTH-B2 · filter: all | onsite | fan_boost
  const [campaignType, setCampaignType] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);       // ad object or {isNew:true}
  const [confirm, setConfirm] = useState(null);
  const [slots, setSlots] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/ads', {
        params: {
          q: q || undefined,
          status: status !== 'all' ? status : undefined,
          ad_type: adType !== 'all' ? adType : undefined,
          campaign_type: campaignType !== 'all' ? campaignType : undefined,
          page, page_size: 20,
        },
      });
      setData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
    finally { setLoading(false); }
  }, [q, status, adType, campaignType, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { (async () => { try { const r = await api.get('/admin/ad-slots'); setSlots(r.data || []); } catch {} })(); }, []);

  const run = async (fn, msg) => {
    try {
      const r = await fn();
      toast.success(msg || r.data?.message || '완료되었습니다.');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || '실패했습니다.';
      console.error('[admin-ads]', e?.response?.status, detail, e?.response?.data);
      toast.error(String(detail));
    }
  };

  const approve = (a) => run(() => api.post(`/admin/ads/${a.ad_id}/approve`));
  const pause   = (a) => run(() => api.post(`/admin/ads/${a.ad_id}/pause`));
  const resume  = (a) => run(() => api.post(`/admin/ads/${a.ad_id}/resume`));
  const end     = (a) => run(() => api.post(`/admin/ads/${a.ad_id}/end`));
  const dup     = (a) => run(() => api.post(`/admin/ads/${a.ad_id}/duplicate`));
  const del     = (a) => setConfirm({ title: '이 광고를 삭제하시겠습니까?', message: `"${a.title}" 캠페인이 삭제됩니다.`, tone: 'danger', confirmLabel: '삭제', onConfirm: () => run(() => api.delete(`/admin/ads/${a.ad_id}`)) });
  const bulkDelete = () => setConfirm({
    title: '테스트 광고를 모두 삭제하시겠습니까?',
    message: 'test_data=true 로 표시된 광고만 삭제됩니다. 이 작업은 되돌리기 어렵습니다.',
    tone: 'danger', confirmLabel: '전부 삭제',
    onConfirm: async () => {
      try { const r = await api.delete('/admin/ads'); toast.success(r.data?.message || '삭제되었습니다.'); load(); }
      catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
    },
  });
  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error('반려 사유는 필수입니다.'); return; }
    try { await api.post(`/admin/ads/${rejectFor.ad_id}/reject`, { reason: rejectReason }); toast.success('광고가 반려되었습니다.'); setRejectFor(null); setRejectReason(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
  };

  const items = data.items || [];

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div>
          <button type="button" onClick={bulkDelete}
            className="text-xs font-bold text-red-600 hover:underline"
            data-testid="ad-bulk-delete-test">테스트 광고 일괄 삭제</button>
        </div>
        <button type="button" onClick={() => setEditing({ isNew: true })}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold" data-testid="ad-new-btn">
          <Plus size={14} /> 광고 등록
        </button>
      </div>

      <FilterBar q={q} setQ={setQ} onSearch={() => { setPage(1); load(); }} onReset={() => { setQ(''); setStatus('all'); setAdType('all'); setCampaignType('all'); setPage(1); }} placeholder="광고명 검색">
        <SelectPill value={campaignType} onChange={(v) => { setCampaignType(v); setPage(1); }} options={[
          { value: 'all',       label: '전체 캠페인' },
          { value: 'onsite',    label: '온사이트 광고' },
          { value: 'fan_boost', label: '단골 늘리기 (FAN BOOST)' },
        ]} testid="filter-campaign-type" />
        <SelectPill value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={[{ value: 'all', label: '모든 상태' }, ...Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))]} testid="filter-status" />
        <SelectPill value={adType} onChange={(v) => { setAdType(v); setPage(1); }} options={[{ value: 'all', label: '모든 유형' }, ...AD_TYPES]} testid="filter-adtype" />
      </FilterBar>

      <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl overflow-hidden" data-testid="admin-ads-table">
        <StateMsg loading={loading} empty={!loading && items.length === 0} emptyText="등록된 광고가 없습니다." />
        {!loading && items.length > 0 && (
          <>
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-black/5 dark:bg-white/5 text-xs uppercase tracking-wider text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-2">광고</th>
                  <th className="px-4 py-2">유형 / 광고주</th>
                  <th className="px-4 py-2">슬롯</th>
                  <th className="px-4 py-2">기간</th>
                  <th className="px-4 py-2">우선순위</th>
                  <th className="px-4 py-2">상태 / 노출</th>
                  <th className="px-4 py-2">클릭</th>
                  <th className="px-4 py-2 text-right">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 dark:divide-white/10">
                {items.map((a) => (
                  <tr key={a.ad_id} data-testid={`ad-row-${a.ad_id}`} className="hover:bg-black/5 dark:hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-14 h-8 bg-gray-100 dark:bg-black/40 rounded overflow-hidden">
                          <SafeImg src={a.image_url} kind="broadcast" alt={a.title} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="font-bold text-sm">{a.title}</div>
                          <div className="text-[11px] text-gray-500">{a.sub_text}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{AD_TYPES.find((t) => t.value === a.ad_type)?.label || a.ad_type || '-'}</div>
                      <div className="text-gray-500">{a.advertiser_name || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{(a.slots || []).map((lk) => lk.slot?.slot_code).filter(Boolean).join(', ') || '-'}</td>
                    <td className="px-4 py-3 text-xs">
                      {a.start_at ? format(new Date(a.start_at), 'M.d HH:mm', { locale: ko }) : '-'}
                      <span className="text-gray-400"> ~ </span>
                      {a.end_at ? format(new Date(a.end_at), 'M.d HH:mm', { locale: ko }) : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs">{a.priority || 0}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[a.effective_status || a.status] || 'bg-gray-500/10'}`}>
                        {STATUS_LABELS[a.effective_status || a.status] || a.status}
                      </span>
                      <div className="mt-1 text-[10px] text-gray-500">{a.is_visible ? '노출' : '숨김'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{a.click_count || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        <button onClick={() => setEditing(a)} className="text-xs font-bold text-blue-600 hover:underline" data-testid={`ad-edit-${a.ad_id}`}>수정</button>
                        {a.status !== 'approved' && a.status !== 'running' && <button onClick={() => approve(a)} className="text-xs font-bold text-emerald-600" data-testid={`ad-approve-${a.ad_id}`}><CheckCircle2 size={11} className="inline" /> 승인</button>}
                        <button onClick={() => setRejectFor(a)} className="text-xs font-bold text-red-600" data-testid={`ad-reject-${a.ad_id}`}><XCircle size={11} className="inline" /> 반려</button>
                        {a.status === 'paused'
                          ? <button onClick={() => resume(a)} className="text-xs font-bold text-emerald-600" data-testid={`ad-resume-${a.ad_id}`}><Play size={11} className="inline" /> 재개</button>
                          : <button onClick={() => pause(a)} className="text-xs font-bold text-amber-600" data-testid={`ad-pause-${a.ad_id}`}><Pause size={11} className="inline" /> 정지</button>}
                        <button onClick={() => end(a)} className="text-xs font-bold text-gray-600" data-testid={`ad-end-${a.ad_id}`}><StopCircle size={11} className="inline" /> 종료</button>
                        <button onClick={() => dup(a)} className="text-xs font-bold text-gray-600" data-testid={`ad-dup-${a.ad_id}`}><Copy size={11} className="inline" /> 복제</button>
                        <button onClick={() => del(a)} className="text-xs font-bold text-red-600" data-testid={`ad-del-${a.ad_id}`}><Trash2 size={11} className="inline" /> 삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="md:hidden divide-y divide-black/5 dark:divide-white/10">
              {items.map((a) => (
                <div key={a.ad_id} className="px-4 py-3" data-testid={`ad-card-${a.ad_id}`}>
                  <div className="flex gap-3">
                    <div className="w-16 h-12 bg-gray-100 dark:bg-black/40 rounded overflow-hidden shrink-0">
                      <SafeImg src={a.image_url} kind="broadcast" alt={a.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{a.title}</div>
                      <div className="text-[11px] text-gray-500 truncate">{a.advertiser_name || '-'} · {AD_TYPES.find((t) => t.value === a.ad_type)?.label || a.ad_type}</div>
                      <div className="text-[11px] mt-1">
                        <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full ${STATUS_TONE[a.effective_status || a.status] || 'bg-gray-500/10'}`}>
                          {STATUS_LABELS[a.effective_status || a.status] || a.status}
                        </span>
                        <span className="ml-2 text-gray-400">클릭 {a.click_count || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button onClick={() => setEditing(a)} className="text-xs font-bold text-blue-600">수정</button>
                    <button onClick={() => approve(a)} className="text-xs font-bold text-emerald-600">승인</button>
                    <button onClick={() => pause(a)} className="text-xs font-bold text-amber-600">정지</button>
                    <button onClick={() => del(a)} className="text-xs font-bold text-red-600 ml-auto">삭제</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Pagination page={page} total={data.total} pageSize={20} onChange={setPage} />

      {editing && <AdEditor user={user} ad={editing.isNew ? null : editing} slots={slots} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {rejectFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 p-5">
            <h3 className="font-black text-lg mb-2">광고 반려</h3>
            <p className="text-sm text-gray-500 mb-3">"{rejectFor.title}" 광고의 반려 사유를 입력해주세요.</p>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
              className="w-full px-3 py-2 border rounded-md bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-sm" data-testid="ad-reject-reason" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setRejectFor(null); setRejectReason(''); }} className="px-3 py-1.5 rounded-full text-xs font-bold bg-black/5 dark:bg-white/10">취소</button>
              <button onClick={submitReject} className="px-3 py-1.5 rounded-full text-xs font-bold bg-red-600 text-white" data-testid="ad-reject-confirm">반려하기</button>
            </div>
          </div>
        </div>
      )}
      <AdminConfirm state={confirm} onClose={() => setConfirm(null)} />
    </>
  );
};

/* ======================== AD EDITOR ======================== */
const AdEditor = ({ ad, slots, onClose, onSaved }) => {
  const isNew = !ad;
  const [form, setForm] = useState(() => ({
    title: ad?.title || '', advertiser_name: ad?.advertiser_name || '', ad_type: ad?.ad_type || 'home_banner',
    image_url: ad?.image_url || '', mobile_image_url: ad?.mobile_image_url || '',
    description: ad?.description || '', sub_text: ad?.sub_text || '', button_text: ad?.button_text || '자세히 보기',
    target_type: ad?.target_type || 'external', target_id: ad?.target_id || '', target_url: ad?.target_url || '',
    start_at: ad?.start_at?.slice(0, 16) || '', end_at: ad?.end_at?.slice(0, 16) || '',
    priority: ad?.priority ?? 0, is_visible: ad?.is_visible ?? true, open_in_new_tab: ad?.open_in_new_tab ?? true,
    admin_memo: ad?.admin_memo || '',
  }));
  const [linkSlots, setLinkSlots] = useState(() => (ad?.slots || []).map((lk) => ({ slot_id: lk.slot_id, priority: lk.priority || 0, is_active: lk.is_active !== false })));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleSlot = (sid) => {
    setLinkSlots((arr) => {
      const has = arr.find((x) => x.slot_id === sid);
      if (has) return arr.filter((x) => x.slot_id !== sid);
      return [...arr, { slot_id: sid, priority: form.priority || 0, is_active: true }];
    });
  };
  const setLinkPriority = (sid, p) => setLinkSlots((arr) => arr.map((x) => x.slot_id === sid ? { ...x, priority: Number(p) || 0 } : x));

  const save = async () => {
    if (!form.title.trim()) { toast.error('광고명은 필수입니다.'); return; }
    setSaving(true);
    try {
      let adId = ad?.ad_id;
      if (isNew) {
        const res = await api.post('/admin/ads', { ...form, slot_ids: linkSlots.map((x) => x.slot_id) });
        adId = res.data?.ad?.ad_id;
      } else {
        await api.put(`/admin/ads/${adId}`, form);
      }
      if (adId && linkSlots.length > 0) {
        await api.put(`/admin/ads/${adId}/slots`, { slots: linkSlots });
      }
      toast.success(isNew ? '광고가 등록되었습니다.' : '광고가 수정되었습니다.');
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || '광고 저장 중 오류가 발생했습니다.';
      console.error('[ad-editor]', e?.response?.status, detail, e?.response?.data);
      toast.error(String(detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ad-editor">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/5 dark:border-white/10">
          <h3 className="font-black text-lg">{isNew ? '광고 등록' : '광고 수정'}</h3>
          <button onClick={onClose} className="p-1"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Field label="광고명 *"><input className={fieldCls} value={form.title} onChange={(e) => set('title', e.target.value)} data-testid="ad-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="광고 유형">
              <select className={fieldCls} value={form.ad_type} onChange={(e) => set('ad_type', e.target.value)} data-testid="ad-adtype">
                {AD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="광고주 / 판매자명"><input className={fieldCls} value={form.advertiser_name} onChange={(e) => set('advertiser_name', e.target.value)} data-testid="ad-advertiser" /></Field>
          </div>

          <Field label="광고 이미지 (PC · 권장 16:5)">
            <ImageUpload value={form.image_url} onChange={(url) => set('image_url', url)} kind="broadcast" testId="ad-img-pc" />
          </Field>
          <Field label="모바일 이미지 (선택 · 권장 4:5)">
            <ImageUpload value={form.mobile_image_url} onChange={(url) => set('mobile_image_url', url)} kind="broadcast" testId="ad-img-mobile" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="광고 문구"><input className={fieldCls} value={form.description} onChange={(e) => set('description', e.target.value)} /></Field>
            <Field label="서브 문구"><input className={fieldCls} value={form.sub_text} onChange={(e) => set('sub_text', e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="버튼 문구"><input className={fieldCls} value={form.button_text} onChange={(e) => set('button_text', e.target.value)} /></Field>
            <Field label="연결 대상 유형">
              <select className={fieldCls} value={form.target_type} onChange={(e) => set('target_type', e.target.value)}>
                {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="연결 대상 ID (선택)"><input className={fieldCls} value={form.target_id} onChange={(e) => set('target_id', e.target.value)} /></Field>
            <Field label="연결 링크"><input className={fieldCls} value={form.target_url} onChange={(e) => set('target_url', e.target.value)} placeholder="https://…" data-testid="ad-target-url" /></Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="시작일"><input type="datetime-local" className={fieldCls} value={form.start_at} onChange={(e) => set('start_at', e.target.value)} /></Field>
            <Field label="종료일"><input type="datetime-local" className={fieldCls} value={form.end_at} onChange={(e) => set('end_at', e.target.value)} /></Field>
            <Field label="우선순위"><input type="number" className={fieldCls} value={form.priority} onChange={(e) => set('priority', Number(e.target.value) || 0)} data-testid="ad-priority" /></Field>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={form.is_visible} onChange={(e) => set('is_visible', e.target.checked)} /> 노출</label>
            <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={form.open_in_new_tab} onChange={(e) => set('open_in_new_tab', e.target.checked)} /> 새 탭 열기</label>
          </div>

          <Field label="관리자 메모">
            <textarea rows={2} className={fieldCls} value={form.admin_memo} onChange={(e) => set('admin_memo', e.target.value)} />
          </Field>

          <div className="pt-3 border-t border-dashed border-black/10 dark:border-white/10">
            <div className="text-xs font-bold text-gray-500 mb-2">연결할 광고 슬롯</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {slots.map((s) => {
                const lk = linkSlots.find((x) => x.slot_id === s.slot_id);
                return (
                  <label key={s.slot_id} className={`flex items-center gap-2 p-2 border rounded-lg ${lk ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}`} data-testid={`ad-slot-check-${s.slot_code}`}>
                    <input type="checkbox" checked={!!lk} onChange={() => toggleSlot(s.slot_id)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold truncate">{s.slot_name}</div>
                      <div className="text-[10px] text-gray-500 truncate">{s.slot_code} · {s.display_type} · {s.aspect_ratio}</div>
                    </div>
                    {lk && (
                      <input type="number" value={lk.priority} onChange={(e) => setLinkPriority(s.slot_id, e.target.value)}
                        className="w-14 px-2 py-1 border rounded text-xs" placeholder="우선순위" />
                    )}
                  </label>
                );
              })}
              {slots.length === 0 && <p className="text-xs text-gray-500 col-span-full">먼저 광고 슬롯 관리 탭에서 슬롯을 추가하세요.</p>}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-black/5 dark:border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold bg-black/5 dark:bg-white/10">취소</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-bold bg-[#FF3B30] text-white hover:bg-[#D32F2F] disabled:opacity-60" data-testid="ad-save">
            <Save size={12} /> {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold text-gray-500 mb-1 block">{label}</label>
    {children}
  </div>
);
const fieldCls = 'w-full px-3 py-2 border rounded-md bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-sm';

/* ======================== SLOTS TAB ======================== */
const SlotsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slot object or {isNew:true}
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/admin/ad-slots'); setItems(r.data || []); }
    catch (e) { toast.error('실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => { try { const r = await fn(); toast.success(msg || r.data?.message || '완료'); load(); } catch (e) { toast.error(e?.response?.data?.detail || '실패'); } };
  const toggle = (s) => run(() => api.put(`/admin/ad-slots/${s.slot_id}`, { is_active: !s.is_active }));
  const del = (s) => setConfirm({
    title: '이 슬롯을 삭제하시겠습니까?',
    message: '사용 중인 슬롯은 비활성화를 우선 사용하세요.', tone: 'danger', confirmLabel: '삭제',
    onConfirm: () => run(() => api.delete(`/admin/ad-slots/${s.slot_id}`)),
  });

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div />
        <button onClick={() => setEditing({ isNew: true })} className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#FF3B30] text-white text-sm font-bold" data-testid="slot-new-btn">
          <Plus size={14} /> 슬롯 추가
        </button>
      </div>

      <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl overflow-hidden" data-testid="admin-slots-table">
        <StateMsg loading={loading} empty={!loading && items.length === 0} emptyText="등록된 슬롯이 없습니다." />
        {!loading && items.length > 0 && (
          <>
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-black/5 dark:bg-white/5 text-xs uppercase tracking-wider text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-2">이름 / 코드</th>
                  <th className="px-4 py-2">페이지 / 위치</th>
                  <th className="px-4 py-2">표시 방식</th>
                  <th className="px-4 py-2">기기</th>
                  <th className="px-4 py-2">비율 / 최대</th>
                  <th className="px-4 py-2">활성</th>
                  <th className="px-4 py-2 text-right">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 dark:divide-white/10">
                {items.map((s) => (
                  <tr key={s.slot_id} data-testid={`slot-row-${s.slot_code}`} className="hover:bg-black/5 dark:hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="font-bold">{s.slot_name}</div>
                      <div className="text-[11px] text-gray-500">{s.slot_code}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{s.page_type} · {s.placement}</td>
                    <td className="px-4 py-3 text-xs">{s.display_type}</td>
                    <td className="px-4 py-3 text-xs">{s.device_type}</td>
                    <td className="px-4 py-3 text-xs">{s.aspect_ratio} · {s.max_items}</td>
                    <td className="px-4 py-3 text-xs">
                      <button onClick={() => toggle(s)} className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${s.is_active ? 'bg-emerald-500/10 text-emerald-600' : 'bg-gray-500/10 text-gray-500'}`} data-testid={`slot-toggle-${s.slot_code}`}>
                        {s.is_active ? '활성' : '비활성'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditing(s)} className="text-blue-600 text-xs font-bold" data-testid={`slot-edit-${s.slot_code}`}>수정</button>
                        <button onClick={() => del(s)} className="text-red-600 text-xs font-bold">삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="md:hidden divide-y divide-black/5 dark:divide-white/10">
              {items.map((s) => (
                <div key={s.slot_id} className="px-4 py-3" data-testid={`slot-card-${s.slot_code}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{s.slot_name}</div>
                      <div className="text-[11px] text-gray-500 truncate">{s.slot_code}</div>
                    </div>
                    <button onClick={() => toggle(s)} className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${s.is_active ? 'bg-emerald-500/10 text-emerald-600' : 'bg-gray-500/10 text-gray-500'}`}>
                      {s.is_active ? '활성' : '비활성'}
                    </button>
                  </div>
                  <div className="mt-1 text-[11px] text-gray-500">{s.page_type} · {s.placement} · {s.display_type} · {s.device_type} · {s.aspect_ratio}</div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setEditing(s)} className="text-blue-600 text-xs font-bold">수정</button>
                    <button onClick={() => del(s)} className="text-red-600 text-xs font-bold ml-auto">삭제</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {editing && <SlotEditor slot={editing.isNew ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <AdminConfirm state={confirm} onClose={() => setConfirm(null)} />
    </>
  );
};

const SlotEditor = ({ slot, onClose, onSaved }) => {
  const isNew = !slot;
  const [form, setForm] = useState({
    slot_code: slot?.slot_code || '', slot_name: slot?.slot_name || '',
    page_type: slot?.page_type || 'home', placement: slot?.placement || 'top',
    display_type: slot?.display_type || 'banner', device_type: slot?.device_type || 'all',
    aspect_ratio: slot?.aspect_ratio || '16:4', max_items: slot?.max_items || 1,
    is_active: slot?.is_active ?? true, description: slot?.description || '', admin_memo: slot?.admin_memo || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.slot_name.trim() || (!isNew ? false : !form.slot_code.trim())) { toast.error('필수 항목을 입력해주세요.'); return; }
    setSaving(true);
    try {
      if (isNew) { const r = await api.post('/admin/ad-slots', form); toast.success(r.data?.message || '광고 슬롯이 저장되었습니다.'); }
      else { const { slot_code, ...rest } = form; const r = await api.put(`/admin/ad-slots/${slot.slot_id}`, rest); toast.success(r.data?.message || '광고 슬롯이 저장되었습니다.'); }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="w-full max-w-lg bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="slot-editor">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/5 dark:border-white/10">
          <h3 className="font-black text-lg">{isNew ? '광고 슬롯 추가' : '광고 슬롯 수정'}</h3>
          <button onClick={onClose} className="p-1"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Field label="슬롯 이름 *"><input className={fieldCls} value={form.slot_name} onChange={(e) => set('slot_name', e.target.value)} data-testid="slot-name" /></Field>
          <Field label={`슬롯 코드 ${isNew ? '*' : '(변경 불가)'}`}>
            <input className={fieldCls} value={form.slot_code} onChange={(e) => set('slot_code', e.target.value)} disabled={!isNew} data-testid="slot-code" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="노출 페이지"><input className={fieldCls} value={form.page_type} onChange={(e) => set('page_type', e.target.value)} placeholder="home / schedule / …" /></Field>
            <Field label="노출 위치"><input className={fieldCls} value={form.placement} onChange={(e) => set('placement', e.target.value)} placeholder="top / above-today-schedule" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="표시 방식">
              <select className={fieldCls} value={form.display_type} onChange={(e) => set('display_type', e.target.value)}>
                <option value="banner">banner</option><option value="card">card</option>
                <option value="carousel">carousel</option><option value="popup">popup</option>
                <option value="inline">inline</option><option value="sticky">sticky</option>
              </select>
            </Field>
            <Field label="기기">
              <select className={fieldCls} value={form.device_type} onChange={(e) => set('device_type', e.target.value)}>
                <option value="all">all</option><option value="pc">pc</option><option value="mobile">mobile</option>
              </select>
            </Field>
            <Field label="이미지 비율"><input className={fieldCls} value={form.aspect_ratio} onChange={(e) => set('aspect_ratio', e.target.value)} placeholder="16:4" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="최대 광고 수"><input type="number" className={fieldCls} value={form.max_items} onChange={(e) => set('max_items', Number(e.target.value) || 1)} /></Field>
            <div className="flex items-end gap-2">
              <label className="inline-flex items-center gap-1 text-xs pb-2"><input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} data-testid="slot-active" /> 활성</label>
            </div>
          </div>
          <Field label="설명"><textarea rows={2} className={fieldCls} value={form.description} onChange={(e) => set('description', e.target.value)} /></Field>
          <Field label="관리자 메모"><textarea rows={2} className={fieldCls} value={form.admin_memo} onChange={(e) => set('admin_memo', e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-black/5 dark:border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold bg-black/5 dark:bg-white/10">취소</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-bold bg-[#FF3B30] text-white disabled:opacity-60" data-testid="slot-save">
            <Save size={12} /> {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAds;
